import React from 'react';
import {
  Card,
  CardContent,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import {
  Delete,
  Add,
  DragIndicator,
  Image,
  AudioFile,
  VideoFile,
} from '@mui/icons-material';
import MediaUploader from '../Media/MediaUploader';

const QuestionEditor = ({ question, index, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) => {
  const handleQuestionChange = (field, value) => {
    onUpdate(index, { ...question, [field]: value });
  };

  const handleOptionChange = (optionIndex, field, value) => {
    const newOptions = [...question.options];
    newOptions[optionIndex] = { ...newOptions[optionIndex], [field]: value };
    onUpdate(index, { ...question, options: newOptions });
  };

  const handleAddOption = () => {
    const newOptions = [...question.options, {
      id: `opt_${Date.now()}`,
      text: '',
      isCorrect: false
    }];
    onUpdate(index, { ...question, options: newOptions });
  };

  const handleDeleteOption = (optionIndex) => {
    const newOptions = question.options.filter((_, i) => i !== optionIndex);
    onUpdate(index, { ...question, options: newOptions });
  };

  const handleCorrectAnswerChange = (optionIndex) => {
    const newOptions = question.options.map((opt, i) => {
      if (question.type === 'single-choice' || question.type === 'true-false') {
        // Pour choix unique, une seule option correcte
        return { ...opt, isCorrect: i === optionIndex };
      } else {
        // Pour choix multiples, toggle
        if (i === optionIndex) {
          return { ...opt, isCorrect: !opt.isCorrect };
        }
        return opt;
      }
    });
    onUpdate(index, { ...question, options: newOptions });
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton size="small" sx={{ cursor: 'grab' }}>
            <DragIndicator />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: 1 }}>
            Question {index + 1}
          </Typography>
          <Chip 
            label={question.points || 1} 
            size="small" 
            color="primary" 
            sx={{ mr: 1 }}
          />
          <IconButton 
            onClick={() => onMoveUp(index)} 
            disabled={isFirst}
            size="small"
          >
            ↑
          </IconButton>
          <IconButton 
            onClick={() => onMoveDown(index)} 
            disabled={isLast}
            size="small"
          >
            ↓
          </IconButton>
          <IconButton onClick={() => onDelete(index)} color="error" size="small">
            <Delete />
          </IconButton>
        </Box>

        {/* Type de question */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Type de question</InputLabel>
          <Select
            value={question.type}
            label="Type de question"
            onChange={(e) => handleQuestionChange('type', e.target.value)}
          >
            <MenuItem value="single-choice">QCM - Choix unique</MenuItem>
            <MenuItem value="multiple-choice">QCM - Choix multiples</MenuItem>
            <MenuItem value="true-false">Vrai / Faux</MenuItem>
            <MenuItem value="open-text">Question ouverte</MenuItem>
          </Select>
        </FormControl>

        {/* Texte de la question */}
        <TextField
          fullWidth
          label="Question"
          multiline
          rows={2}
          value={question.question}
          onChange={(e) => handleQuestionChange('question', e.target.value)}
          sx={{ mb: 2 }}
          required
        />

        {/* Description optionnelle */}
        <TextField
          fullWidth
          label="Description (optionnel)"
          multiline
          rows={2}
          value={question.description || ''}
          onChange={(e) => handleQuestionChange('description', e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Médias */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Média (optionnel)
          </Typography>
          <MediaUploader
            existingMedia={question.media}
            onUploadComplete={(media) => handleQuestionChange('media', media)}
            onDelete={() => handleQuestionChange('media', { type: null, url: '', filename: '', size: 0 })}
            label="Ajouter un média (image, audio ou vidéo)"
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Options pour QCM et Vrai/Faux */}
        {(question.type === 'single-choice' || 
          question.type === 'multiple-choice' || 
          question.type === 'true-false') && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Options de réponse
            </Typography>
            {question.options.map((option, optionIndex) => (
              <Box 
                key={option.id} 
                sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  mb: 1, 
                  alignItems: 'center',
                  p: 1,
                  bgcolor: option.isCorrect ? 'success.lighter' : 'transparent',
                  borderRadius: 1,
                  border: option.isCorrect ? '2px solid' : '1px solid',
                  borderColor: option.isCorrect ? 'success.main' : 'divider'
                }}
              >
                <Typography sx={{ minWidth: 30, textAlign: 'center', fontWeight: 'bold' }}>
                  {String.fromCharCode(65 + optionIndex)}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={option.text}
                  onChange={(e) => handleOptionChange(optionIndex, 'text', e.target.value)}
                  placeholder="Texte de l'option"
                  required
                />
                <Button
                  variant={option.isCorrect ? 'contained' : 'outlined'}
                  color="success"
                  size="small"
                  onClick={() => handleCorrectAnswerChange(optionIndex)}
                  sx={{ minWidth: 100 }}
                >
                  {option.isCorrect ? '✓ Correcte' : 'Correcte ?'}
                </Button>
                {question.type !== 'true-false' && (
                  <IconButton 
                    onClick={() => handleDeleteOption(optionIndex)}
                    size="small"
                    color="error"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}

            {question.type !== 'true-false' && (
              <Button
                startIcon={<Add />}
                onClick={handleAddOption}
                variant="outlined"
                size="small"
                sx={{ mt: 1 }}
              >
                Ajouter une option
              </Button>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Explication et points */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            label="Explication de la réponse (optionnel)"
            multiline
            rows={2}
            value={question.explanation || ''}
            onChange={(e) => handleQuestionChange('explanation', e.target.value)}
          />
          <TextField
            label="Points"
            type="number"
            value={question.points || 1}
            onChange={(e) => handleQuestionChange('points', parseInt(e.target.value) || 1)}
            sx={{ width: 100 }}
            inputProps={{ min: 1, max: 10 }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export default QuestionEditor;

